import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { TextField } from '@mui/material';
import { IconSearch } from 'assets/icons';
import axios from '../../axios';
import AddAgent from 'components/AgentModel';
import AgentDetailView from '../../components/AgentDetailView';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Paper
} from '@mui/material';
import Spinner from '../../components/Spinner';
import PageHeader from 'components/PageHeader';

const useStyles = createUseStyles({
    container: {
        // padding: '20px',
        backgroundColor: '#ffffff',
        minHeight: '100vh'
    },
    headerSection: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        // alignItems: 'center',
        marginBottom: '20px',
        backgroundColor: '#F9FAFB',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 8px 8px rgba(0, 0, 0, 0.15)'
    },
    pageTitle: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#1f2937',
        margin: 0
    },
    contentSection: {
        width: '100%'
    }
});

function Agents() {
    const classes = useStyles();
    const [agents, setAgents] = useState([]);
    const [allAgents, setAllAgents] = useState([]); // Store all agents for search
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchWord, setSearchWord] = useState('');
    const [selectedAgent, setSelectedAgent] = useState({});
    const [delOpen, setDelOpen] = useState(false);
    const [suspendOpen, setSuspendOpen] = useState(false);
    const [unsuspendOpen, setUnSuspendOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [viewingAgentId, setViewingAgentId] = useState(null);
    const [confirmationText, setConfirmationText] = useState('');

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = () => {
        setLoading(true);
        axios
            .get('/admin/agents')
            .then((response) => {
                const filteredAgents = response.data.data.filter((user) => user.role === 'agent');
                setAgents(filteredAgents);
                setAllAgents(filteredAgents);
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to fetch agents');
                setLoading(false);
            });
    };

    const getSearchAgents = () => {
        if (searchWord !== '' && allAgents.length > 0) {
            const searchResults = allAgents.filter((agent) => {
                return (
                    agent.name.toLowerCase().includes(searchWord.toLowerCase()) ||
                    agent.email.toLowerCase().includes(searchWord.toLowerCase()) ||
                    agent.contact.toLowerCase().includes(searchWord.toLowerCase())
                );
            });

            if (searchResults.length > 0) {
                setAgents(searchResults);
                setError('');
            } else {
                setError(`No agents found matching "${searchWord}"`);
                setAgents([]);
            }
        } else {
            setAgents(allAgents);
            setError('');
        }
    };

    const handleDelete = () => {
        setDeleting(true);
        axios
            .delete(`/admin/agent/${selectedAgent._id}`)
            .then(() => {
                setDeleting(false);
                setDelOpen(false);
                fetchAgents();
            })
            .catch(() => {
                setDeleting(false);
                setError('Failed to delete agent');
            });
    };

    const handleSuspend = () => {
        if (!selectedAgent._id) {
            setError('No agent selected for suspension');
            return;
        }
        axios
            .post(`/admin/agent/suspend/${selectedAgent._id}`)
            .then(() => {
                setSuspendOpen(false);
                fetchAgents();
            })
            .catch(() => {
                setError('Failed to suspend agent');
            });
    };

    const handleUnSuspend = () => {
        if (!selectedAgent._id) {
            setError('No agent selected for unsuspension');
            return;
        }
        axios
            .post(`/admin/agent/unsuspend/${selectedAgent._id}`)
            .then(() => {
                setUnSuspendOpen(false);
                fetchAgents();
            })
            .catch(() => {
                setError('Failed to unsuspend agent');
            });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            getSearchAgents();
        }
    };

    const customTableCellStyle = {
        padding: '12px',
        fontSize: '13px',
        fontWeight: 500,
        borderBottom: '1px solid #f1f5f9',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '150px'
    };

    // If viewing an agent, show the detail view
    if (viewingAgentId) {
        return (
            <AgentDetailView
                agentId={viewingAgentId}
                onBack={() => {
                    setViewingAgentId(null);
                    fetchAgents(); // Refresh the list when coming back
                }}
            />
        );
    }

    return (
        <div className={classes.container}>
            {/* Header Section */}
            <PageHeader
                title='Agents'
                subtitle='Manage your platform agents efficiently'
                variant='gradient'
            />
            {/* Add Agent and Search */}
            <div className={classes.headerSection}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <AddAgent />

                    {/* Search Bar */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px'
                        }}
                        className='SearchDiv'
                    >
                        <TextField
                            required
                            id='outlined-required'
                            label='Search Agents'
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        borderColor: '#d1d5db'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#085d40'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#085d40'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280'
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#085d40'
                                }
                            }}
                            placeholder='Name, email or contact'
                            onChange={(e) => {
                                setSearchWord(e.target.value);
                                setError('');
                                if (e.target.value === '') {
                                    setAgents(allAgents);
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            value={searchWord}
                        />

                        <div
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#085d40',
                                color: '#ffffff',
                                padding: '10px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #085d40',
                                '&:hover': {
                                    backgroundColor: '#064532'
                                }
                            }}
                            onClick={() => {
                                getSearchAgents();
                            }}
                        >
                            <IconSearch color='#ffffff' />
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.contentSection}>
                {loading ? (
                    <div
                        style={{
                            width: '100%',
                            height: '300px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        className='CenterSpinner'
                    >
                        <Spinner size='big' />
                    </div>
                ) : agents.length === 0 && !error ? (
                    <div
                        style={{
                            width: '100%',
                            height: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6b7280',
                            fontSize: '16px',
                            fontWeight: 500
                        }}
                    >
                        No agents found
                    </div>
                ) : (
                    <TableContainer
                        component={Paper}
                        sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            // boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden'
                        }}
                    >
                        <Table
                            sx={{
                                tableLayout: 'fixed',
                                width: '100%'
                            }}
                            aria-label='agents table'
                        >
                            <TableHead
                                sx={{
                                    backgroundColor: '#085d40', // Dark green header
                                    borderBottom: '2px solid #064532'
                                }}
                            >
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#ffffff',
                                            textTransform: 'uppercase',
                                            padding: '12px 16px',
                                            width: '80px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        No.
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#ffffff',
                                            textTransform: 'uppercase',
                                            padding: '12px 16px',
                                            width: '160px'
                                        }}
                                    >
                                        Name
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#ffffff',
                                            textTransform: 'uppercase',
                                            padding: '12px 16px',
                                            width: '180px'
                                        }}
                                    >
                                        Email
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#ffffff',
                                            textTransform: 'uppercase',
                                            padding: '12px 16px',
                                            width: '120px'
                                        }}
                                    >
                                        Contact
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#ffffff',
                                            textTransform: 'uppercase',
                                            padding: '12px 16px',
                                            width: '100px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#ffffff',
                                            textTransform: 'uppercase',
                                            padding: '12px 16px',
                                            width: '200px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agents.map((agent, index) => (
                                    <TableRow
                                        key={agent._id}
                                        sx={{
                                            backgroundColor: '#ffffff',
                                            '&:hover': {
                                                backgroundColor: '#f8fafc',
                                                transition: 'background-color 0.2s ease'
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#f9fafb'
                                            }
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                ...customTableCellStyle,
                                                width: '80px',
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                color: '#6b7280'
                                            }}
                                            component='th'
                                            scope='row'
                                        >
                                            {index + 1}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...customTableCellStyle,
                                                width: '160px',
                                                color: '#1f2937',
                                                fontWeight: 600
                                            }}
                                            component='th'
                                            scope='row'
                                            title={agent.name}
                                        >
                                            {agent.name}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...customTableCellStyle,
                                                width: '180px',
                                                color: '#1f2937',
                                                fontFamily: 'monospace'
                                            }}
                                            component='th'
                                            scope='row'
                                            title={agent.email}
                                        >
                                            {agent.email}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...customTableCellStyle,
                                                width: '120px',
                                                color: '#1f2937',
                                                fontFamily: 'monospace'
                                            }}
                                            component='th'
                                            scope='row'
                                            title={agent.contact}
                                        >
                                            {agent.contact}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...customTableCellStyle,
                                                width: '100px',
                                                textAlign: 'center'
                                            }}
                                            component='th'
                                            scope='row'
                                        >
                                            <span
                                                style={{
                                                    backgroundColor: agent.suspended
                                                        ? '#fef2f2'
                                                        : '#ecfdf5',
                                                    color: agent.suspended ? '#dc2626' : '#059669',
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    border: `1px solid ${
                                                        agent.suspended ? '#fecaca' : '#a7f3d0'
                                                    }`
                                                }}
                                            >
                                                {agent.suspended ? 'Suspended' : 'Active'}
                                            </span>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...customTableCellStyle,
                                                width: '200px',
                                                textAlign: 'center'
                                            }}
                                            component='th'
                                            scope='row'
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '6px',
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <button
                                                    onClick={() => setViewingAgentId(agent._id)}
                                                    style={{
                                                        border: 'none',
                                                        outline: 'none',
                                                        cursor: 'pointer',
                                                        backgroundColor: '#2563eb',
                                                        color: '#ffffff',
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        transition: 'all 0.2s ease',
                                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = '#1d4ed8';
                                                        e.target.style.transform = 'scale(1.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = '#2563eb';
                                                        e.target.style.transform = 'scale(1)';
                                                    }}
                                                    title='View agent details'
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedAgent(agent);
                                                        setDelOpen(true);
                                                    }}
                                                    style={{
                                                        border: 'none',
                                                        outline: 'none',
                                                        cursor: 'pointer',
                                                        backgroundColor: '#dc2626',
                                                        color: '#ffffff',
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        transition: 'all 0.2s ease',
                                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = '#b91c1c';
                                                        e.target.style.transform = 'scale(1.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = '#dc2626';
                                                        e.target.style.transform = 'scale(1)';
                                                    }}
                                                    title='Delete agent'
                                                >
                                                    Delete
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedAgent(agent);
                                                        if (agent.suspended) {
                                                            setUnSuspendOpen(true);
                                                        } else {
                                                            setSuspendOpen(true);
                                                        }
                                                    }}
                                                    style={{
                                                        border: 'none',
                                                        outline: 'none',
                                                        cursor: 'pointer',
                                                        backgroundColor: agent.suspended
                                                            ? '#059669'
                                                            : '#f59e0b',
                                                        color: '#ffffff',
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        transition: 'all 0.2s ease',
                                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor =
                                                            agent.suspended ? '#047857' : '#d97706';
                                                        e.target.style.transform = 'scale(1.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor =
                                                            agent.suspended ? '#059669' : '#f59e0b';
                                                        e.target.style.transform = 'scale(1)';
                                                    }}
                                                    title={
                                                        agent.suspended
                                                            ? 'Unsuspend agent'
                                                            : 'Suspend agent'
                                                    }
                                                >
                                                    {agent.suspended ? 'Unsuspend' : 'Suspend'}
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {error && (
                    <div
                        style={{
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            color: '#dc2626',
                            fontSize: '14px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        ⚠️ {error}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={delOpen}
                onClose={() => {
                    setDelOpen(false);
                    setConfirmationText('');
                }}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '12px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ color: '#1f2937', fontWeight: 600 }}>Confirm Delete</DialogTitle>
                <DialogContent sx={{ color: '#6b7280', padding: '20px 24px' }}>
                    <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                        Are you sure you want to delete "<strong>{selectedAgent.name}</strong>"?
                        This action cannot be undone.
                    </p>
                    <p
                        style={{
                            margin: '0 0 12px 0',
                            color: '#374151',
                            fontSize: '14px',
                            fontWeight: 500
                        }}
                    >
                        Type "<strong>{selectedAgent.name}</strong>" to confirm this action.
                    </p>
                    <TextField
                        fullWidth
                        placeholder={selectedAgent.name}
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        variant='outlined'
                        size='small'
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                '& fieldset': {
                                    borderColor: '#d1d5db'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#085d40'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#085d40'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button
                        onClick={() => {
                            setDelOpen(false);
                            setConfirmationText('');
                        }}
                        variant='outlined'
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant='contained'
                        disabled={deleting || confirmationText !== selectedAgent.name}
                        sx={{
                            backgroundColor: '#dc2626',
                            '&:hover': {
                                backgroundColor: '#b91c1c'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#9ca3af',
                                color: '#ffffff'
                            }
                        }}
                    >
                        {deleting ? <Spinner /> : 'Delete Agent'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Suspend Confirmation Dialog */}
            <Dialog
                open={suspendOpen}
                onClose={() => setSuspendOpen(false)}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '12px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ color: '#1f2937', fontWeight: 600 }}>
                    Confirm Suspend
                </DialogTitle>
                <DialogContent sx={{ color: '#6b7280' }}>
                    Are you sure you want to suspend {selectedAgent.name}?
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button
                        onClick={() => setSuspendOpen(false)}
                        variant='outlined'
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSuspend}
                        variant='contained'
                        sx={{
                            backgroundColor: '#f59e0b',
                            '&:hover': {
                                backgroundColor: '#d97706'
                            }
                        }}
                    >
                        Suspend
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unsuspend Confirmation Dialog */}
            <Dialog
                open={unsuspendOpen}
                onClose={() => setUnSuspendOpen(false)}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '12px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ color: '#1f2937', fontWeight: 600 }}>
                    Confirm Unsuspend
                </DialogTitle>
                <DialogContent sx={{ color: '#6b7280' }}>
                    Are you sure you want to unsuspend {selectedAgent.name}?
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button
                        onClick={() => setUnSuspendOpen(false)}
                        variant='outlined'
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUnSuspend}
                        variant='contained'
                        sx={{
                            backgroundColor: '#059669',
                            '&:hover': {
                                backgroundColor: '#047857'
                            }
                        }}
                    >
                        Unsuspend
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Agents;
